import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  User, Calendar, Home, FileText, CheckCircle2, AlertCircle, Users, ChevronRight,
  Upload, Camera, Check, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const translations = {
  it: {
    title: 'La Maisonette di Paestum',
    subtitle: 'Check-in Online',
    error: 'Errore',
    invalidLink: 'Link non valido o scaduto',
    guests: 'ospiti',
    mainGuest: 'Ospite Principale',
    mainGuestDesc: 'Inserisci i dati dell\'intestatario della prenotazione',
    firstName: 'Nome',
    lastName: 'Cognome',
    birthDate: 'Data di Nascita',
    birthPlace: 'Luogo di Nascita',
    nationality: 'Nazionalità',
    docType: 'Tipo Documento',
    docNumber: 'Numero Documento',
    idCard: 'Carta d\'Identità',
    passport: 'Passaporto',
    drivingLicense: 'Patente di Guida',
    continue: 'Continua',
    otherGuests: 'Altri Ospiti',
    otherGuestsDesc: 'Inserisci i dati degli altri ospiti',
    noOtherGuests: 'Non ci sono altri ospiti da registrare',
    guestNum: 'Ospite',
    stayFor1: 'Soggiorno per 1 persona',
    back: 'Indietro',
    houseRules: 'Regolamento della Casa',
    houseRulesDesc: 'Leggi e accetta il regolamento',
    acceptRules: 'Dichiaro di aver letto e accetto il regolamento della struttura *',
    digitalSignature: 'Confermo che i dati inseriti sono corretti (firma digitale)',
    completeCheckin: 'Completa Check-in',
    submitting: 'Invio in corso...',
    checkinComplete: 'Check-in Completato!',
    thankYou: 'Grazie per aver completato il check-in online. Ti aspettiamo a La Maisonette di Paestum!',
    bookingSummary: 'Riepilogo prenotazione:',
    checkinTime: 'Check-in',
    checkoutTime: 'Check-out',
    from: 'dalle 15:00',
    until: 'entro le 10:00',
    contactUs: 'Per qualsiasi domanda contattaci al +39 393 4957532',
    fillRequired: 'Compila tutti i campi obbligatori',
    mustAcceptRules: 'Devi accettare il regolamento',
    checkinSuccess: 'Check-in completato!',
    checkinError: 'Errore durante il check-in',
    documentPhoto: 'Foto Documento',
    documentPhotoDesc: 'Carica fronte e retro del documento',
    front: 'Fronte',
    backDoc: 'Retro',
    upload: 'Carica',
    uploading: 'Caricamento...',
    uploadSuccess: 'Foto caricata!',
    uploadError: 'Errore caricamento',
    frontOk: 'Fronte OK',
    backOk: 'Retro OK',
    retake: 'Rifai',
    requiredPhotos: 'Carica entrambe le foto del documento'
  },
  en: {
    title: 'La Maisonette di Paestum',
    subtitle: 'Online Check-in',
    error: 'Error',
    invalidLink: 'Invalid or expired link',
    guests: 'guests',
    mainGuest: 'Main Guest',
    mainGuestDesc: 'Enter the booking holder\'s details',
    firstName: 'First Name',
    lastName: 'Last Name',
    birthDate: 'Date of Birth',
    birthPlace: 'Place of Birth',
    nationality: 'Nationality',
    docType: 'Document Type',
    docNumber: 'Document Number',
    idCard: 'ID Card',
    passport: 'Passport',
    drivingLicense: 'Driving License',
    continue: 'Continue',
    otherGuests: 'Other Guests',
    otherGuestsDesc: 'Enter the other guests\' details',
    noOtherGuests: 'No other guests to register',
    guestNum: 'Guest',
    stayFor1: 'Stay for 1 person',
    back: 'Back',
    houseRules: 'House Rules',
    houseRulesDesc: 'Read and accept the rules',
    acceptRules: 'I declare that I have read and accept the property rules *',
    digitalSignature: 'I confirm that the information entered is correct (digital signature)',
    completeCheckin: 'Complete Check-in',
    submitting: 'Submitting...',
    checkinComplete: 'Check-in Complete!',
    thankYou: 'Thank you for completing your online check-in. We look forward to welcoming you at La Maisonette di Paestum!',
    bookingSummary: 'Booking summary:',
    checkinTime: 'Check-in',
    checkoutTime: 'Check-out',
    from: 'from 3:00 PM',
    until: 'by 10:00 AM',
    contactUs: 'For any questions contact us at +39 393 4957532',
    fillRequired: 'Please fill in all required fields',
    mustAcceptRules: 'You must accept the rules',
    checkinSuccess: 'Check-in completed!',
    checkinError: 'Error during check-in',
    documentPhoto: 'Document Photo',
    documentPhotoDesc: 'Upload front and back of document',
    front: 'Front',
    backDoc: 'Back',
    upload: 'Upload',
    uploading: 'Uploading...',
    uploadSuccess: 'Photo uploaded!',
    uploadError: 'Upload error',
    frontOk: 'Front OK',
    backOk: 'Back OK',
    retake: 'Retake',
    requiredPhotos: 'Upload both document photos'
  }
};

// Get browser language
const getBrowserLanguage = () => {
  const lang = navigator.language || navigator.userLanguage;
  return lang.startsWith('it') ? 'it' : 'en';
};

export default function OnlineCheckin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checkinData, setCheckinData] = useState(null);
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);
  const [language] = useState(getBrowserLanguage());
  const t = translations[language] || translations.it;
  
  // Photo upload states
  const [uploadingFronte, setUploadingFronte] = useState(false);
  const [uploadingRetro, setUploadingRetro] = useState(false);
  
  const [form, setForm] = useState({
    ospite_principale: {
      nome: '',
      cognome: '',
      data_nascita: '',
      luogo_nascita: '',
      nazionalita: 'Italiana',
      documento_tipo: 'carta_identita',
      documento_numero: '',
      foto_fronte_url: '',
      foto_retro_url: ''
    },
    altri_ospiti: [],
    accettazione_regole: false,
    firma_digitale: false
  });

  useEffect(() => {
    fetchCheckinData();
  }, [token]);

  const fetchCheckinData = async () => {
    try {
      const response = await axios.get(`${API}/online-checkin/${token}`);
      setCheckinData(response.data);
      
      if (response.data.checkin_status === 'completed') {
        setStep(4); // Already completed
      }
      
      // Pre-fill name if available
      if (response.data.booking?.nome_ospite) {
        const parts = response.data.booking.nome_ospite.split(' ');
        setForm(prev => ({
          ...prev,
          ospite_principale: {
            ...prev.ospite_principale,
            nome: parts[0] || '',
            cognome: parts.slice(1).join(' ') || ''
          }
        }));
      }
      
      // Add empty slots for additional guests
      const numOspiti = response.data.booking?.num_ospiti || 1;
      if (numOspiti > 1) {
        const altriOspiti = [];
        for (let i = 1; i < numOspiti; i++) {
          altriOspiti.push({
            nome: '',
            cognome: '',
            data_nascita: '',
            nazionalita: language === 'en' ? 'Italian' : 'Italiana'
          });
        }
        setForm(prev => ({ ...prev, altri_ospiti: altriOspiti }));
      }
    } catch (error) {
      setError(t.invalidLink);
    } finally {
      setLoading(false);
    }
  };

  const updateOspitePrincipale = (field, value) => {
    setForm(prev => ({
      ...prev,
      ospite_principale: { ...prev.ospite_principale, [field]: value }
    }));
  };

  // Handle document photo upload
  const handlePhotoUpload = async (file, type) => {
    if (!file) return;
    
    const setUploading = type === 'fronte' ? setUploadingFronte : setUploadingRetro;
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Use public endpoint for checkin document upload (no auth required)
      const response = await axios.post(`${API}/upload/checkin-document`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000
      });
      
      const url = response.data.url;
      setForm(prev => ({
        ...prev,
        ospite_principale: {
          ...prev.ospite_principale,
          [type === 'fronte' ? 'foto_fronte_url' : 'foto_retro_url']: url
        }
      }));
      toast.success(t.uploadSuccess);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(t.uploadError);
    } finally {
      setUploading(false);
    }
  };

  const updateAltroOspite = (index, field, value) => {
    const newAltri = [...form.altri_ospiti];
    newAltri[index] = { ...newAltri[index], [field]: value };
    setForm(prev => ({ ...prev, altri_ospiti: newAltri }));
  };

  const validateStep = (stepNum) => {
    if (stepNum === 1) {
      const { nome, cognome, data_nascita, documento_numero, foto_fronte_url, foto_retro_url } = form.ospite_principale;
      if (!nome || !cognome || !data_nascita || !documento_numero) {
        toast.error(t.fillRequired);
        return false;
      }
      // Require document photos
      if (!foto_fronte_url || !foto_retro_url) {
        toast.error(t.requiredPhotos);
        return false;
      }
    }
    if (stepNum === 3) {
      if (!form.accettazione_regole) {
        toast.error(t.mustAcceptRules);
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const submitCheckin = async () => {
    if (!validateStep(3)) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/online-checkin/${token}`, form);
      toast.success(t.checkinSuccess);
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || t.checkinError);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8f5f0] to-[#e8e0d5] flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C5A059]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8f5f0] to-[#e8e0d5] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">{t.error}</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8f5f0] to-[#e8e0d5] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-cinzel text-3xl text-[#1A202C] mb-2">{t.title}</h1>
          <p className="text-gray-600">{t.subtitle}</p>
        </div>

        {/* Booking Info */}
        {checkinData?.booking && step < 4 && (
          <Card className="mb-6 bg-[#C5A059]/10 border-[#C5A059]/30">
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <Home className="w-8 h-8 text-[#C5A059]" />
                <div>
                  <p className="font-medium">{checkinData.unit?.nome || 'La Maisonette'}</p>
                  <p className="text-sm text-gray-600">
                    {new Date(checkinData.booking.data_arrivo).toLocaleDateString(language === 'en' ? 'en-US' : 'it-IT')} → {new Date(checkinData.booking.data_partenza).toLocaleDateString(language === 'en' ? 'en-US' : 'it-IT')}
                    <span className="mx-2">•</span>
                    {checkinData.booking.num_ospiti} {t.guests}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Steps */}
        {step < 4 && (
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s ? 'bg-[#C5A059] text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {s}
                </div>
                {s < 3 && <div className={`w-12 h-1 ${step > s ? 'bg-[#C5A059]' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
        )}

        {/* Step 1: Ospite Principale */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-[#C5A059]" />
                {t.mainGuest}
              </CardTitle>
              <CardDescription>{t.mainGuestDesc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t.firstName} *</Label>
                  <Input
                    value={form.ospite_principale.nome}
                    onChange={(e) => updateOspitePrincipale('nome', e.target.value)}
                    placeholder="Mario"
                  />
                </div>
                <div>
                  <Label>{t.lastName} *</Label>
                  <Input
                    value={form.ospite_principale.cognome}
                    onChange={(e) => updateOspitePrincipale('cognome', e.target.value)}
                    placeholder="Rossi"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t.birthDate} *</Label>
                  <Input
                    type="date"
                    value={form.ospite_principale.data_nascita}
                    onChange={(e) => updateOspitePrincipale('data_nascita', e.target.value)}
                  />
                </div>
                <div>
                  <Label>{t.birthPlace} *</Label>
                  <Input
                    value={form.ospite_principale.luogo_nascita}
                    onChange={(e) => updateOspitePrincipale('luogo_nascita', e.target.value)}
                    placeholder="Roma"
                  />
                </div>
              </div>
              
              <div>
                <Label>{t.nationality} *</Label>
                <Input
                  value={form.ospite_principale.nazionalita}
                  onChange={(e) => updateOspitePrincipale('nazionalita', e.target.value)}
                  placeholder={language === 'en' ? 'Italian' : 'Italiana'}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t.docType} *</Label>
                  <select
                    className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.ospite_principale.documento_tipo}
                    onChange={(e) => updateOspitePrincipale('documento_tipo', e.target.value)}
                  >
                    <option value="carta_identita">{t.idCard}</option>
                    <option value="passaporto">{t.passport}</option>
                    <option value="patente">{t.drivingLicense}</option>
                  </select>
                </div>
                <div>
                  <Label>{t.docNumber} *</Label>
                  <Input
                    value={form.ospite_principale.documento_numero}
                    onChange={(e) => updateOspitePrincipale('documento_numero', e.target.value)}
                    placeholder="AA1234567"
                  />
                </div>
              </div>
              
              {/* Document Photo Upload Section */}
              <div className="space-y-3 pt-4 border-t">
                <Label className="text-base font-medium">{t.documentPhoto} *</Label>
                <p className="text-sm text-gray-500">{t.documentPhotoDesc}</p>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Front Document */}
                  <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    form.ospite_principale.foto_fronte_url ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-[#C5A059]'
                  }`}>
                    {uploadingFronte ? (
                      <div className="py-4">
                        <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin mx-auto mb-2" />
                        <p className="text-sm text-gray-500">{t.uploading}</p>
                      </div>
                    ) : form.ospite_principale.foto_fronte_url ? (
                      <div className="py-2">
                        <Check className="w-10 h-10 text-green-600 mx-auto mb-1" />
                        <p className="text-sm font-medium text-green-700">{t.frontOk}</p>
                        <label className="text-xs text-[#C5A059] underline cursor-pointer mt-2 inline-block">
                          {t.retake}
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => e.target.files?.[0] && handlePhotoUpload(e.target.files[0], 'fronte')}
                          />
                        </label>
                      </div>
                    ) : (
                      <div className="py-2">
                        <p className="text-sm font-medium text-gray-700 mb-3">{t.front}</p>
                        <label className="inline-flex items-center gap-2 bg-[#C5A059] text-white px-4 py-2 rounded-lg text-sm cursor-pointer hover:bg-[#B08A3E] transition-colors">
                          <Camera className="w-4 h-4" />
                          {t.upload}
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => e.target.files?.[0] && handlePhotoUpload(e.target.files[0], 'fronte')}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                  
                  {/* Back Document */}
                  <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    form.ospite_principale.foto_retro_url ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-[#C5A059]'
                  }`}>
                    {uploadingRetro ? (
                      <div className="py-4">
                        <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin mx-auto mb-2" />
                        <p className="text-sm text-gray-500">{t.uploading}</p>
                      </div>
                    ) : form.ospite_principale.foto_retro_url ? (
                      <div className="py-2">
                        <Check className="w-10 h-10 text-green-600 mx-auto mb-1" />
                        <p className="text-sm font-medium text-green-700">{t.backOk}</p>
                        <label className="text-xs text-[#C5A059] underline cursor-pointer mt-2 inline-block">
                          {t.retake}
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => e.target.files?.[0] && handlePhotoUpload(e.target.files[0], 'retro')}
                          />
                        </label>
                      </div>
                    ) : (
                      <div className="py-2">
                        <p className="text-sm font-medium text-gray-700 mb-3">{t.backDoc}</p>
                        <label className="inline-flex items-center gap-2 bg-[#C5A059] text-white px-4 py-2 rounded-lg text-sm cursor-pointer hover:bg-[#B08A3E] transition-colors">
                          <Camera className="w-4 h-4" />
                          {t.upload}
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => e.target.files?.[0] && handlePhotoUpload(e.target.files[0], 'retro')}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <Button onClick={nextStep} className="w-full bg-[#C5A059] hover:bg-[#B08A3E]">
                {t.continue}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Altri Ospiti */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#C5A059]" />
                {t.otherGuests}
              </CardTitle>
              <CardDescription>
                {form.altri_ospiti.length > 0 
                  ? t.otherGuestsDesc 
                  : t.noOtherGuests}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {form.altri_ospiti.map((ospite, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <h4 className="font-medium text-gray-700">{t.guestNum} {idx + 2}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{t.firstName}</Label>
                      <Input
                        value={ospite.nome}
                        onChange={(e) => updateAltroOspite(idx, 'nome', e.target.value)}
                        placeholder={t.firstName}
                      />
                    </div>
                    <div>
                      <Label>{t.lastName}</Label>
                      <Input
                        value={ospite.cognome}
                        onChange={(e) => updateAltroOspite(idx, 'cognome', e.target.value)}
                        placeholder={t.lastName}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{t.birthDate}</Label>
                      <Input
                        type="date"
                        value={ospite.data_nascita}
                        onChange={(e) => updateAltroOspite(idx, 'data_nascita', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>{t.nationality}</Label>
                      <Input
                        value={ospite.nazionalita}
                        onChange={(e) => updateAltroOspite(idx, 'nazionalita', e.target.value)}
                        placeholder={language === 'en' ? 'Italian' : 'Italiana'}
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {form.altri_ospiti.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t.stayFor1}</p>
                </div>
              )}
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={prevStep} className="flex-1">
                  {t.back}
                </Button>
                <Button onClick={nextStep} className="flex-1 bg-[#C5A059] hover:bg-[#B08A3E]">
                  {t.continue}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Regolamento e Conferma */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#C5A059]" />
                {t.houseRules}
              </CardTitle>
              <CardDescription>{t.houseRulesDesc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* House Rules */}
              <div className="max-h-60 overflow-y-auto border rounded-lg p-4 bg-gray-50 space-y-3">
                {checkinData?.house_rules?.map((rule, idx) => (
                  <div key={idx}>
                    <h4 className="font-medium text-gray-800">{rule.titolo}</h4>
                    <p className="text-sm text-gray-600">{rule.contenuto}</p>
                  </div>
                ))}
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                  <Checkbox
                    id="accettazione"
                    checked={form.accettazione_regole}
                    onCheckedChange={(checked) => setForm({...form, accettazione_regole: checked})}
                  />
                  <label htmlFor="accettazione" className="text-sm text-gray-700 cursor-pointer">
                    {t.acceptRules}
                  </label>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                  <Checkbox
                    id="firma"
                    checked={form.firma_digitale}
                    onCheckedChange={(checked) => setForm({...form, firma_digitale: checked})}
                  />
                  <label htmlFor="firma" className="text-sm text-gray-700 cursor-pointer">
                    {t.digitalSignature}
                  </label>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={prevStep} className="flex-1">
                  {t.back}
                </Button>
                <Button 
                  onClick={submitCheckin} 
                  disabled={submitting || !form.accettazione_regole}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {submitting ? t.submitting : t.completeCheckin}
                  <CheckCircle2 className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Completato */}
        {step === 4 && (
          <Card>
            <CardContent className="pt-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{t.checkinComplete}</h2>
              <p className="text-gray-600 mb-6">
                {t.thankYou}
              </p>
              
              {checkinData?.booking && (
                <div className="p-4 bg-[#C5A059]/10 rounded-lg text-left">
                  <h3 className="font-medium text-[#C5A059] mb-2">{t.bookingSummary}</h3>
                  <p className="text-sm text-gray-700">
                    <strong>{t.checkinTime}:</strong> {new Date(checkinData.booking.data_arrivo).toLocaleDateString(language === 'en' ? 'en-US' : 'it-IT')} {t.from}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>{t.checkoutTime}:</strong> {new Date(checkinData.booking.data_partenza).toLocaleDateString(language === 'en' ? 'en-US' : 'it-IT')} {t.until}
                  </p>
                </div>
              )}
              
              <p className="text-sm text-gray-500 mt-6">
                {t.contactUs}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
